import { FC, PropsWithChildren, useEffect, useMemo, useRef } from "react";

import { useLoc, useSchool, useSettings } from "./hooks";
import { useLangDetector } from "./hooks/useLangDetector";
import { useUser } from "./hooks/useUser";

type SiteFormTermsSettings = {
  enabled: boolean;
  defaultAccepted: boolean;
  terms: string;
};

type QuizSettings = {
  scriptSrc: string;
  styleSrc: string;
  lang: string;
  config: {
    apiUrl: string;
    queryArgs?: () => Record<string, string>;
    isAuthorized?: boolean;
  };
  loc: Record<string, string>;
};

export type FormSettings = {
  // This is used in sites also. dont forget to update there
  loc: Record<string, string>;
};

export type AXLData = {
  siteId?: string;
  sitePageId?: string;
  widgetId?: string;
  orderId?: string;
  contactId?: string;
  courseId?: string;
  courseItemId?: string;
  libraryId?: string;
  libraryItemId?: string;
  formTermsSettings?: SiteFormTermsSettings;
  checkoutFormTermsSettings?: SiteFormTermsSettings;
  quizSettings: QuizSettings;
  formSettings: FormSettings;
};

declare global {
  interface Window {
    AXLData?: AXLData;
  }
}

type Props = {
  courseId?: string;
  courseItemId?: string;
  libraryId?: string;
  libraryItemId?: string;
};

export const AxlDataProvider: FC<PropsWithChildren<Props>> = ({
  courseId,
  courseItemId,
  libraryId,
  libraryItemId,
  children,
}) => {
  const langDetector = useLangDetector();
  const { user } = useUser();
  const school = useSchool();
  const loc = useLoc();

  const settings = useSettings();

  const argsRef = useRef({
    courseId,
    courseItemId,
    libraryId,
    libraryItemId,
  });
  const axlDataRef = useRef<AXLData | undefined>(undefined);

  useMemo(() => {
    const axlData: AXLData = {
      contactId: user?.id,
      courseId,
      courseItemId,
      libraryId,
      libraryItemId,
      formSettings: {
        loc: {
          invalidEmail: loc.word("Form.invalidEmail", {
            default: "Enter valid email",
          }),
          invalidPhone: loc.word("Form.invalidPhoneNumberFormat", {
            default: "Enter valid phone number",
          }),
          invalidNumber: loc.word("Form.invalidNumber", {
            default: "Enter valid number",
          }),
          invalidUrl: loc.word("Form.invalidUrl", {
            default: "Enter valid url",
          }),
          required: loc.word("Form.requiredField", { default: "Required" }),
        },
      },
      quizSettings: {
        styleSrc: settings.quiz.styleUrl,
        scriptSrc: settings.quiz.scriptUrl,
        config: {
          apiUrl: settings.quiz.apiUrl.replace(
            "{schoolNumber}",
            school.number.toString(),
          ),
          isAuthorized: true,
          queryArgs: () => {
            const args: Record<string, string> = {};
            if (user?.id) args.a_c = user.id;

            if (argsRef.current.courseId) args.c_id = argsRef.current.courseId;

            if (argsRef.current.courseItemId)
              args.ci_id = argsRef.current.courseItemId;

            if (argsRef.current.libraryId)
              args.l_id = argsRef.current.libraryId;

            if (argsRef.current.libraryItemId)
              args.li_id = argsRef.current.libraryItemId;

            return args;
          },
        },
        lang: langDetector.lang,
        loc: {
          noVideoPlaceholder: loc.word("Form.noVideoPlaceholder", {
            default: "No video selected",
          }),
          noImagePlaceholder: loc.word("Form.noImagePlaceholder", {
            default: "No image selected",
          }),
          maxFileSizeExceeded: loc.word("Form.maxFileSizeExceeded", {
            default: "Max file size exceeded",
          }),
          waitForFileUpload: loc.word("Form.waitForFileUpload", {
            default: "Wait for the file to upload completely",
          }),
          invalidUrl: loc.word("Form.invalidUrl", {
            default: "Invalid url format",
          }),
          invalidPhoneNumberFormat: loc.word("Form.invalidPhoneNumberFormat", {
            default: "Invalid phone number format",
          }),
          invalidNumberMin: loc.word("Form.invalidNumberMin", {
            default: "Number must be less than",
          }),
          invalidNumberMax: loc.word("Form.invalidNumberMax", {
            default: "Number must be greater than",
          }),
          noSelectOptions: loc.word("Form.noSelectOptions", {
            default: "No options yet",
          }),
          requiredField: loc.word("Form.requiredField", {
            default: "Required field",
          }),
          invalidEmail: loc.word("Form.invalidEmail", {
            default: "Invalid email format",
          }),
          noItemsPlaceholder: loc.word("Form.noItemsPlaceholder", {
            default: "No items yet. Try to refresh this page later...",
          }),
          backBtnLabel: loc.word("Form.backBtnLabel", { default: "Back" }),
          quizNotFound: loc.word("Form.notFoundLabel", {
            default: "Quiz not found. Try to refresh this page later...",
          }),
          quizCompleted: loc.word("Form.completedLabel", {
            default: "Quiz completed successfully",
          }),
          completeMsg: loc.word("Form.completeMsg", { default: "Submit" }),
          nextMsg: loc.word("Form.nextMsg", { default: "Next" }),
          loading: loc.word("Form.loadingLabel", { default: "Loading..." }),
          itemHiddenLabel: loc.word("Form.itemHiddenLabel", {
            default: "Hidden",
          }),
          itemHiddenHelp: loc.word("Form.itemHiddenHelp", {
            default:
              "User did not see this field because it is hidden according to settings",
          }),
          deleteFileConfirm: loc.word("Form.deleteFileConfirm", {
            default: "Are you sure you want to delete this file?",
          }),
          downloadFile: loc.word("Form.downloadFile", { default: "Download" }),
          uploadFileHelp: loc.word("Form.uploadFileHelp", {
            default: "Click or drag file to this area to upload",
          }),
          uploadFileRestrictions: loc.word("Form.uploadFileRestrictions", {
            default: "Upload up to 10 files, maximum size is",
          }),
          invalidFileFormat: loc.word("Form.invalidFileFormat", {
            default: "Invalid file format",
          }),
          noFiles: loc.word("Form.noFiles", { default: "No files yet" }),
        },
      },
    };
    axlDataRef.current = axlData;
    window.AXLData = axlData;
  }, []);

  useEffect(() => () => {
    if (window.AXLData === axlDataRef.current) window.AXLData = undefined;
  }, []);

  useEffect(() => {
    argsRef.current = {
      courseId,
      courseItemId,
      libraryId,
      libraryItemId,
    };
    const data = window.AXLData;
    if (!data) return;
    data.courseId = courseId;
    data.courseItemId = courseItemId;
    data.libraryId = libraryId;
    data.libraryItemId = libraryItemId;
  }, [courseId, courseItemId, libraryId, libraryItemId]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
